import { ThumbDownOutlined, ThumbUpOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useEffect, useState } from 'react';

import { feedbackCreate } from '~/data/generated/endpoints/feedback/feedback';
import type { FeedbackRequest as FeedbackBody } from '~/data/generated/model';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

interface FeedbackReason {
  key: string;
  label: string;
}

interface FeedbackProps {
  feedbackKey: string;
  runId: string | null;
  placeholder?: string;
  /**
   * Optional preset reasons rendered as multi-select chips above the
   * comment field. Selected chip keys are sent as `reasons` on the
   * feedback request and stored in LangSmith feedback metadata.
   * Omitting this prop preserves the original chip-less behavior used
   * by the contract chat.
   */
  reasonOptions?: ReadonlyArray<FeedbackReason>;
  /**
   * Fires after the feedback API call resolves successfully. Used by
   * callers that need to mirror the submission to product analytics.
   */
  onSubmitted?: (result: { isPositive: boolean; reasons: string[] }) => void;
}

type ActionType = 'submit' | 'skip' | null;

const FEEDBACK_SCORES = {
  POSITIVE: 1,
  NEGATIVE: 0,
} as const;

const Feedback = ({
  feedbackKey,
  runId,
  placeholder = 'Is this insight helpful?',
  reasonOptions,
  onSubmitted,
}: FeedbackProps) => {
  const { showSnackbar, showEntityActionSnackbar } = useLAISnackbar();
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackState, setFeedbackState] = useState({
    isOpen: false,
    feedbackText: '',
    isPositive: null as boolean | null,
    currentAction: null as ActionType,
  });
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

  const toggleReason = (key: string) => {
    setSelectedReasons((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const onFeedback = async (opts: { comment: string; isPositive: boolean; reasons: string[] }) => {
    try {
      setIsSubmittingFeedback(true);
      const score = opts.isPositive ? FEEDBACK_SCORES.POSITIVE : FEEDBACK_SCORES.NEGATIVE;
      const feedback: FeedbackBody = {
        runId: runId ?? '',
        key: feedbackKey,
        comment: opts.comment,
        score,
        ...(opts.reasons.length > 0 ? { reasons: opts.reasons } : {}),
      };
      await feedbackCreate(feedback);

      // Isolate consumer-supplied callbacks from the submit flow. The
      // callback is typed `(...) => void` but TypeScript's `void`
      // return is permissive — a future caller could write an async
      // handler whose rejection would otherwise escape the try/catch
      // (which only catches sync throws on a non-awaited call) and
      // mis-report the API-successful submission as a failure.
      // `Promise.resolve(x)` normalizes either return shape into a
      // chain we can attach a catch handler to.
      Promise.resolve(onSubmitted?.({ isPositive: opts.isPositive, reasons: opts.reasons })).catch(
        captureException,
      );

      showEntityActionSnackbar(
        {
          name: 'Feedback',
          action: ActionEnum.Create,
          id: 'submitted successfully',
        },
        {
          variant: 'success',
        },
      );
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      captureException(error);
      showEntityActionSnackbar(
        {
          name: 'Feedback',
          action: ActionEnum.Fail,
          id: 'submission failed',
        },
        {
          variant: 'error',
        },
      );
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const { isOpen, feedbackText, isPositive, currentAction } = feedbackState;

  const handleScoreClick = (positive: boolean) => {
    setFeedbackState((prev) => ({ ...prev, isOpen: true, isPositive: positive }));
  };

  const handleSubmit = async () => {
    if (isPositive === null) return;
    setFeedbackState((prev) => ({ ...prev, currentAction: 'submit' }));
    try {
      await onFeedback({
        comment: feedbackText.trim(),
        isPositive,
        reasons: selectedReasons,
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      captureException(error);
      showSnackbar('An unexpected error occurred. Please try again later.', { variant: 'error' });
    }
  };

  const handleSkip = async () => {
    if (isPositive === null) return;
    setFeedbackState((prev) => ({ ...prev, currentAction: 'skip' }));
    try {
      await onFeedback({ comment: '', isPositive, reasons: selectedReasons });
    } catch (error) {
      console.error('Failed to skip feedback:', error);
      captureException(error);
      showSnackbar('An unexpected error occurred. Please try again later.', { variant: 'error' });
    }
  };

  const handleClose = () => {
    if (isSubmittingFeedback) return;
    setFeedbackState({ isOpen: false, feedbackText: '', isPositive: null, currentAction: null });
    setSelectedReasons([]);
  };

  useEffect(() => {
    if (!isSubmittingFeedback) {
      setFeedbackState({ isOpen: false, feedbackText: '', isPositive: null, currentAction: null });
      setSelectedReasons([]);
    }
  }, [isSubmittingFeedback]);

  return (
    <>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
        <Typography variant="caption" color="primary">
          {placeholder}
        </Typography>
        <IconButton
          size="small"
          color="lightButton"
          onClick={() => handleScoreClick(true)}
          disabled={isSubmittingFeedback || !runId}
          aria-label="Helpful"
        >
          <ThumbUpOutlined fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color="lightButton"
          onClick={() => handleScoreClick(false)}
          disabled={isSubmittingFeedback || !runId}
          aria-label="Not Helpful"
        >
          <ThumbDownOutlined fontSize="small" />
        </IconButton>
      </Box>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-describedby="feedback-dialog-description"
        role="dialog"
      >
        <DialogTitle sx={{ marginTop: 1 }}>
          {isPositive ? 'What was helpful?' : 'What could be improved?'}
        </DialogTitle>
        <DialogContent id="feedback-dialog-description">
          {/* Reason chips are failure-mode-flavored ("inaccurate data",
              "hallucinated", …) so they only make sense on the 👎
              path. On 👍 we just take the optional free-text comment. */}
          {reasonOptions && reasonOptions.length > 0 && isPositive === false ? (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {reasonOptions.map((r) => {
                const selected = selectedReasons.includes(r.key);
                return (
                  <Chip
                    key={r.key}
                    label={r.label}
                    onClick={() => toggleReason(r.key)}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    disabled={isSubmittingFeedback}
                  />
                );
              })}
            </Stack>
          ) : null}
          <TextField
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder="Optional feedback..."
            value={feedbackText}
            onChange={(e) =>
              setFeedbackState((prev) => ({ ...prev, feedbackText: e.target.value }))
            }
            disabled={isSubmittingFeedback}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSkip}
            variant="text"
            disabled={isSubmittingFeedback}
            sx={BUTTON_SX}
          >
            Skip
            {isSubmittingFeedback && currentAction === 'skip' ? (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            ) : null}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmittingFeedback}
            sx={BUTTON_SX}
          >
            Submit
            {isSubmittingFeedback && currentAction === 'submit' ? (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            ) : null}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Feedback;
